/**
 * Single source of truth for thumbnail video playback.
 *
 * Every Thumbnail registers its <video> element here. The controller decides
 * which (at most one) video should be playing and pauses everything else.
 *
 * Decision (per evaluate):
 *   if (!enabled) -> nothing
 *   else if (modalOpen || tabHidden) -> nothing
 *   else if (desktop && hoveredId) -> that one
 *   else if (interacting) -> nothing
 *   else if (activePostIndex != null) -> the entry matching it
 *   else -> nothing
 *
 * NotAllowedError: if play() rejects (e.g. iOS without prior gesture), retry
 * on the next user gesture.
 */

interface VideoEntry {
  id: string;
  el: HTMLVideoElement;
  postIndex: number;
}

type GestureEventName = 'pointerdown' | 'touchstart' | 'keydown';
const GESTURE_EVENTS: GestureEventName[] = ['pointerdown', 'touchstart', 'keydown'];

export const createAutoplayController = () => {
  const entries = new Map<string, VideoEntry>();

  const state = {
    enabled: true,
    isMobile: false,
    activePostIndex: null as number | null,
    hoveredId: null as string | null,
    modalOpen: false,
    tabHidden: typeof document !== 'undefined' ? document.hidden : false,
    interacting: false,
  };

  let evaluateScheduled = false;
  let retryAttached = false;

  const chooseTarget = (): VideoEntry | null => {
    if (!state.enabled) return null;
    if (state.modalOpen || state.tabHidden) return null;

    if (!state.isMobile && state.hoveredId) {
      const entry = entries.get(state.hoveredId);
      if (entry) return entry;
    }

    if (state.interacting) return null;
    if (state.activePostIndex == null) return null;

    let match: VideoEntry | null = null;
    entries.forEach((entry) => {
      if (match === null && entry.postIndex === state.activePostIndex) match = entry;
    });
    return match;
  };

  const attachRetry = () => {
    if (retryAttached || typeof document === 'undefined') return;
    retryAttached = true;
    const retry = () => {
      retryAttached = false;
      GESTURE_EVENTS.forEach((evt) => document.removeEventListener(evt, retry, true));
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      schedule();
    };
    GESTURE_EVENTS.forEach((evt) => document.addEventListener(
      evt,
      retry,
      { once: true, capture: true },
    ));
  };

  const evaluate = () => {
    const target = chooseTarget();

    entries.forEach((entry) => {
      if (entry !== target && !entry.el.paused) {
        try { entry.el.pause(); } catch { /* ignore */ }
      }
    });

    if (!target) return;

    const { el } = target;
    const p = el.play() as Promise<void> | undefined;
    if (p && typeof p.then === 'function') {
      p.catch((err: unknown) => {
        const name = (err as { name?: string } | null)?.name;
        if (name === 'NotAllowedError') attachRetry();
      });
    }
  };

  const schedule = () => {
    if (evaluateScheduled) return;
    evaluateScheduled = true;
    if (typeof window === 'undefined') { evaluateScheduled = false; return; }
    window.requestAnimationFrame(() => {
      evaluateScheduled = false;
      evaluate();
    });
  };

  const handleVisibility = () => {
    state.tabHidden = document.hidden;
    schedule();
  };

  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', handleVisibility);
  }

  const setIfChanged = <K extends keyof typeof state>(key: K, value: typeof state[K]) => {
    if (state[key] === value) return;
    state[key] = value;
    schedule();
  };

  return {
    destroy: () => {
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', handleVisibility);
      }
      entries.clear();
    },

    register: (id: string, el: HTMLVideoElement, postIndex: number) => {
      const existing = entries.get(id);
      if (existing && existing.el === el && existing.postIndex === postIndex) return;
      entries.set(id, { id, el, postIndex });
      schedule();
    },

    unregister: (id: string) => {
      const entry = entries.get(id);
      if (entry) {
        try { entry.el.pause(); } catch { /* ignore */ }
      }
      entries.delete(id);
      if (state.hoveredId === id) state.hoveredId = null;
      schedule();
    },

    setEnabled: (v: boolean) => setIfChanged('enabled', v),
    setIsMobile: (v: boolean) => setIfChanged('isMobile', v),
    setActivePostIndex: (v: number | null) => setIfChanged('activePostIndex', v),
    setModalOpen: (v: boolean) => setIfChanged('modalOpen', v),
    setInteracting: (v: boolean) => setIfChanged('interacting', v),

    setHovered: (id: string) => {
      if (state.hoveredId === id) return;
      state.hoveredId = id;
      schedule();
    },

    clearHoveredIfMatches: (id: string) => {
      if (state.hoveredId !== id) return;
      state.hoveredId = null;
      schedule();
    },
  };
};

export type AutoplayController = ReturnType<typeof createAutoplayController>;
